import { isPlatformBrowser } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  PLATFORM_ID,
  AfterViewInit,
  ChangeDetectorRef,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  TemplateRef
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { SafeHtml, DomSanitizer } from "@angular/platform-browser";
import { AppActionTypes } from "@app/store/actions/app.actions";
import {
  ApproveNestedComment,
  CreateNestedComment,
  DeleteNestedComment,
  UpdateNestedComment,
  ApproveNestedCommentFailure,
  ApproveNestedCommentSuccess,
  UpdateNestedCommentSuccess
} from "@app/store/actions/nested-comments.actions";
import { CreateTogglePropertySuccess, DeleteTogglePropertySuccess } from "@app/store/actions/toggle-property.actions";
import { MainState } from "@app/store/state";
import { ContentTypeInterface } from "@core/interfaces/content-type.interface";
import { NestedCommentInterface } from "@core/interfaces/nested-comment.interface";
import { TogglePropertyInterface } from "@core/interfaces/toggle-property.interface";
import { UserInterface } from "@core/interfaces/user.interface";
import { ClassicRoutesService } from "@core/services/classic-routes.service";
import { ContentTranslateService } from "@core/services/content-translate.service";
import { DeviceService } from "@core/services/device.service";
import { HighlightService } from "@core/services/highlight.service";
import { LoadingService } from "@core/services/loading.service";
import { PopNotificationsService } from "@core/services/pop-notifications.service";
import { RouterService } from "@core/services/router.service";
import { UserService } from "@core/services/user.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { ofType, Actions } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { BaseComponentDirective } from "@shared/components/base-component.directive";
import { ReportAbuseModalComponent } from "@shared/components/report-abuse-modal/report-abuse-modal.component";
import { filter, map, take, takeUntil, tap } from "rxjs/operators";

@Component({
  selector: "astrobin-nested-comment",
  templateUrl: "./nested-comment.component.html",
  styleUrls: ["./nested-comment.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NestedCommentComponent
  extends BaseComponentDirective
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @Input()
  comment: NestedCommentInterface;

  @Input()
  highlighted = false;

  // Whether to show the reply button or not. Some usages of this component might prefer flat comments.
  @Input()
  showReplyButton = true;

  // Whether to allow the user to reply to their own comments.
  @Input()
  allowSelfReply = true;

  // Weather the user can moderate the comment.
  @Input()
  allowModeration = false;

  @Input()
  commentContentType: ContentTypeInterface;

  // Whether to restrict the reply to a specific user. Useful for question/answer scenarios where you only want to owner
  // of an object to reply to a comment.
  @Input()
  restrictReplyToUserId: UserInterface["id"];

  @Input()
  category?: string;

  @Input()
  useOffcanvasOnTouchDevices = false;

  @Output()
  formDirtyChange = new EventEmitter<boolean>();

  @ViewChild(ReportAbuseModalComponent)
  reportAbuseModal?: ReportAbuseModalComponent;

  @ViewChild("replyOffcanvasTemplate")
  replyOffcanvasTemplate: TemplateRef<any>;

  replyModel: {
    commentReply: string; // JSON from TipTap
    commentReplyHtml?: string; // HTML from TipTap
    commentReplyText?: string; // Plain text from TipTap
  } = { commentReply: '{"type":"doc","content":[{"type":"paragraph"}]}' };
  replyForm = new FormGroup({});
  replyFields: FormlyFieldConfig[];
  replyOffcanvasFields: FormlyFieldConfig[];
  showReplyForm = false;
  expandedOffcanvasEditor = false;

  editModel: {
    comment: string; // JSON from TipTap
    commentHtml?: string; // HTML from TipTap
    commentText?: string; // Plain text from TipTap
  } = { comment: "" };
  editForm = new FormGroup({});
  editFields: FormlyFieldConfig[];
  showEditForm = false;

  translating = false;
  translated = false;

  protected approving = false;
  protected deleting = false;
  protected _isSubmittingReply = false;
  protected link: string;
  protected margin = `0px`;
  protected userGalleryUrl: string;
  protected avatarUrl: string;
  protected html: SafeHtml;

  private readonly _isBrowser: boolean;
  private _elementWidth: number;

  constructor(
    public readonly store$: Store<MainState>,
    public readonly actions$: Actions,
    public readonly translateService: TranslateService,
    public readonly loadingService: LoadingService,
    public readonly windowRefService: WindowRefService,
    public readonly routerService: RouterService,
    public readonly elementRef: ElementRef,
    public readonly classicRoutesService: ClassicRoutesService,
    public readonly userService: UserService,
    public readonly utilsService: UtilsService,
    @Inject(PLATFORM_ID) public readonly platformId: Object,
    public readonly changeDetectorRef: ChangeDetectorRef,
    public readonly popNotificationsService: PopNotificationsService,
    public readonly deviceService: DeviceService,
    public readonly contentTranslateService: ContentTranslateService,
    public readonly highlightService: HighlightService,
    public readonly domSanitizer: DomSanitizer,
    public readonly offcanvasService: NgbOffcanvas
  ) {
    super(store$);
    this._isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this._updateHtml();
    this._initReplyFields();
    this._initReplyOffcanvasFields();
    this._initEditFields();
    this._initHighlighted();
    this._listenToLikes();
    this._setupFormListeners();
  }

  isDirty(): boolean {
    const replyValue = this.replyForm.get("commentReply")?.value;
    const editValue = this.editForm.get("comment")?.value;

    // Reply always uses TipTap
    const hasReplyContent =
      this.showReplyForm &&
      this.replyForm.dirty &&
      !!replyValue &&
      typeof replyValue === "string" &&
      !!(replyValue as string).trim() &&
      replyValue !== '{"type":"doc","content":[{"type":"paragraph"}]}';

    // Edit might use either TipTap or CKEditor
    let hasEditContent = false;
    if (this.showEditForm && this.editForm.dirty && editValue) {
      const isNewStyleComment = !!this.comment?.textProsemirror;
      if (isNewStyleComment) {
        // TipTap: check for non-empty JSON
        hasEditContent =
          !!editValue &&
          typeof editValue === "string" &&
          !!(editValue as string).trim() &&
          editValue !== '{"type":"doc","content":[{"type":"paragraph"}]}';
      } else {
        // CKEditor: just check for non-empty string
        hasEditContent = !!editValue && typeof editValue === "string" && !!(editValue as string).trim();
      }
    }

    return hasReplyContent || hasEditContent;
  }

  ngAfterViewInit() {
    if (!this._isBrowser) {
      return;
    }

    this._elementWidth = this.elementRef.nativeElement.getBoundingClientRect().width;
    this._initMarginLeft();
    this.changeDetectorRef.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.comment && changes.comment.currentValue) {
      this._initLink();
      this._initMarginLeft();
      this._initUserGalleryUrl();
      this._initAvatarUrl();
      this._updateHtml(); // Re-run _updateHtml which conditionally calls _initHighlightJs
    }
  }

  cancelEdit() {
    this.editForm.reset();
    this.showEditForm = false;
    this.changeDetectorRef.markForCheck();
  }

  submitEdit() {
    this.actions$
      .pipe(
        ofType(AppActionTypes.UPDATE_NESTED_COMMENT_SUCCESS),
        filter((action: UpdateNestedCommentSuccess) => action.payload.nestedComment.id === this.comment.id),
        map((action: UpdateNestedCommentSuccess) => action.payload.nestedComment),
        take(1),
        tap(() => this.cancelEdit())
      )
      .subscribe(comment => {
        this.comment = comment;
        this._updateHtml();
        this.changeDetectorRef.markForCheck();
      });

    const isNewStyleComment = !!this.comment.textProsemirror;
    const commentValue = this.editForm.get("comment").value;

    let updatePayload: any;

    if (isNewStyleComment) {
      // New style: send all three formats
      updatePayload = {
        ...this.comment,
        textProsemirror: commentValue,
        textHtml: this.editModel.commentHtml || "",
        text: this.editModel.commentText || "",
        html: this.editModel.commentHtml || "" // Keep for backward compatibility
      };
    } else {
      // Legacy style: send BBCode in text field
      updatePayload = {
        ...this.comment,
        text: commentValue,
        html: this.comment.html // Keep existing HTML
      };
    }

    this.store$.dispatch(
      new UpdateNestedComment({
        nestedComment: updatePayload
      })
    );
  }

  cancelReply() {
    // Mark form as untouched FIRST to prevent validation errors
    this.replyForm.markAsUntouched();

    // Then reset and update model
    this.replyModel = { commentReply: '{"type":"doc","content":[{"type":"paragraph"}]}' };
    this.replyForm.reset();
    this.replyForm.markAsPristine();

    this.showReplyForm = false;
    this.changeDetectorRef.markForCheck();
  }

  openReplyOffcanvas(): void {
    // Initialize offcanvas-specific reply fields with unique ID
    this._initReplyOffcanvasFields();
    this.replyModel = { commentReply: '{"type":"doc","content":[{"type":"paragraph"}]}' };

    if (!this.replyOffcanvasTemplate) {
      // Wait for next tick and try again
      this.utilsService.delay(0).subscribe(() => {
        if (this.replyOffcanvasTemplate) {
          this._openOffcanvasNow();
        }
      });
      return;
    }

    this._openOffcanvasNow();
  }

  private _openOffcanvasNow(): void {
    const offcanvasRef = this.offcanvasService.open(this.replyOffcanvasTemplate, {
      panelClass: "nested-comment-reply-offcanvas image-viewer-offcanvas",
      backdropClass: "image-viewer-offcanvas-backdrop",
      position: "bottom"
    });

    // Focus immediately on offcanvas shown to preserve user gesture context
    offcanvasRef.shown.subscribe(() => {
      this._focusTipTapEditor();
    });

    // Reset form after offcanvas is opened and FormlyForm is rendered
    this.utilsService.delay(0).subscribe(() => {
      this.replyForm.markAsUntouched();
      this.replyForm.reset();
      this.replyForm.markAsPristine();
    });

    offcanvasRef.result.then(
      () => {
        // Offcanvas closed normally
      },
      () => {
        // Offcanvas dismissed - check if form is dirty
        this._handleOffcanvasDismissal();
      }
    );
  }

  submitReplyOffcanvas(): void {
    this.submitReply();
  }

  private _focusTipTapEditor(): void {
    if (!this._isBrowser) {
      return;
    }

    // Try to focus immediately to preserve user gesture context for mobile
    const tryFocus = () => {
      const editor = document.querySelector(".nested-comment-reply-offcanvas .tiptap-content .ProseMirror");
      if (editor && editor instanceof HTMLElement) {
        editor.focus();
        return true;
      }
      return false;
    };

    // First attempt - synchronous
    if (tryFocus()) {
      return;
    }

    // If not found immediately, try a few more times quickly
    // but keep it within the gesture context timeframe
    let attempts = 0;
    const quickRetry = () => {
      if (attempts >= 3 || tryFocus()) {
        return;
      }
      attempts++;
      this.utilsService.delay(10).subscribe(quickRetry);
    };

    quickRetry();
  }

  private _handleOffcanvasDismissal(): void {
    // Check if form has content that would be lost
    const hasContent = this._hasFormContent();

    if (hasContent) {
      // Show confirmation dialog
      const confirmDiscard = confirm(
        this.translateService.instant("You have unsaved changes. Are you sure you want to discard them?")
      );

      if (confirmDiscard) {
        this.cancelReply();
      } else {
        // Reopen the offcanvas since user chose to keep editing
        this.openReplyOffcanvas();
      }
    } else {
      // No content to lose, safe to cancel
      this.cancelReply();
    }
  }

  private _hasFormContent(): boolean {
    try {
      const value = this.replyForm.get("commentReply")?.value;
      if (!value || typeof value !== "string") {
        return false;
      }

      const json = JSON.parse(value);

      // Check if content is more than just empty paragraph
      return !(
        json?.type === "doc" &&
        json?.content?.length === 1 &&
        json?.content[0]?.type === "paragraph" &&
        (!json?.content[0]?.content || json?.content[0]?.content.length === 0)
      );
    } catch {
      return false;
    }
  }

  toggleExpandedOffcanvasEditor(): void {
    this.expandedOffcanvasEditor = !this.expandedOffcanvasEditor;
  }

  submitReply() {
    if (this._isSubmittingReply) {
      return;
    }

    this._isSubmittingReply = true;

    // Disable the form control (which will disable the TipTap editor)
    this.replyForm.get("commentReply")?.disable();

    this.changeDetectorRef.markForCheck();

    this.currentUser$.pipe(take(1)).subscribe(user => {
      if (!user) {
        this._isSubmittingReply = false;

        // Re-enable the form control
        this.replyForm.get("commentReply")?.enable();

        this.changeDetectorRef.markForCheck();
        void this.routerService.redirectToLogin();
        return;
      }

      const textProsemirror = this.replyForm.get("commentReply").value;
      const textHtml = this.replyModel.commentReplyHtml || "";
      const text = this.replyModel.commentReplyText || "";

      this.store$.dispatch(
        new CreateNestedComment({
          nestedComment: {
            contentType: this.comment.contentType,
            objectId: this.comment.objectId,
            textProsemirror: textProsemirror,
            textHtml: textHtml,
            text: text,
            html: textHtml, // Keep for backward compatibility
            parent: this.comment.id,
            category: this.category || this.comment.category
          }
        })
      );

      this.actions$
        .pipe(
          ofType(AppActionTypes.CREATE_NESTED_COMMENT_SUCCESS, AppActionTypes.CREATE_NESTED_COMMENT_FAILURE),
          take(1)
        )
        .subscribe((action: any) => {
          this._isSubmittingReply = false;

          // Re-enable the form control
          this.replyForm.get("commentReply")?.enable();

          this.changeDetectorRef.markForCheck();

          if (action.type === AppActionTypes.CREATE_NESTED_COMMENT_SUCCESS) {
            this.cancelReply();
            // Close offcanvas if on mobile
            if (this.deviceService.xsMax()) {
              this.offcanvasService.dismiss();
            }
          }
        });
    });
  }

  onApproveClicked(event: Event) {
    event.preventDefault();

    if (!this.comment.pendingModeration) {
      return;
    }

    this.approving = true;

    this.actions$
      .pipe(
        ofType(AppActionTypes.APPROVE_NESTED_COMMENT_SUCCESS),
        map((action: ApproveNestedCommentSuccess) => action.payload),
        filter(payload => payload.nestedComment.id === this.comment.id),
        take(1)
      )
      .subscribe(() => {
        this.approving = false;
        this.changeDetectorRef.markForCheck();
      });

    this.actions$
      .pipe(
        ofType(AppActionTypes.APPROVE_NESTED_COMMENT_FAILURE),
        map((action: ApproveNestedCommentFailure) => action.payload),
        filter(payload => payload.id === this.comment.id),
        take(1)
      )
      .subscribe(() => {
        this.approving = false;
        this.changeDetectorRef.markForCheck();
      });

    this.store$.dispatch(new ApproveNestedComment({ id: this.comment.id }));
    this.changeDetectorRef.markForCheck();
  }

  onDeleteClicked(event: Event) {
    event.preventDefault();

    if (this.comment.deleted) {
      return;
    }

    this.deleting = true;

    this.actions$
      .pipe(
        ofType(AppActionTypes.DELETE_NESTED_COMMENT_SUCCESS, AppActionTypes.DELETE_NESTED_COMMENT_FAILURE),
        map(() => this.comment.id),
        filter(id => id === this.comment.id),
        take(1)
      )
      .subscribe(() => {
        this.deleting = false;
        this.changeDetectorRef.markForCheck();
      });

    this.store$.dispatch(new DeleteNestedComment({ id: this.comment.id }));
    this.changeDetectorRef.markForCheck();
  }

  onEditClicked(event: Event) {
    event.preventDefault();

    if (this.comment.deleted) {
      return;
    }

    // Check if this is a new-style comment with ProseMirror content
    const isNewStyleComment = !!this.comment.textProsemirror;

    if (isNewStyleComment) {
      // New style: use ProseMirror JSON
      this.editModel = {
        comment: this.comment.textProsemirror,
        commentHtml: this.comment.textHtml || this.comment.html,
        commentText: this.comment.text
      };
    } else {
      // Legacy style: use BBCode text
      this.editModel = {
        comment: this.comment.text,
        commentHtml: this.comment.html,
        commentText: this.comment.text
      };
    }

    // Re-initialize fields with the appropriate editor type
    this._initEditFields();
    this.showEditForm = true;
    this.changeDetectorRef.markForCheck();
  }

  onReplyClicked(event: Event) {
    event.preventDefault();

    if (this.comment.deleted || this.comment.pendingModeration) {
      return;
    }

    this.currentUser$.pipe(take(1)).subscribe(user => {
      if (!user) {
        void this.routerService.redirectToLogin();
        return;
      }

      if (
        this._isBrowser &&
        this.deviceService.isTouchEnabled() &&
        this.deviceService.xsMax() &&
        this.useOffcanvasOnTouchDevices
      ) {
        this.openReplyOffcanvas();
      } else {
        this.showReplyForm = true;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  async onShareClicked(event: Event) {
    event.preventDefault();

    const isNativeShareSupported = typeof navigator !== "undefined" && !!navigator.share;
    const isMobile = this.deviceService.isMobile();

    if (isNativeShareSupported && isMobile) {
      try {
        navigator
          .share({
            title: this.link,
            url: this.link
          })
          .catch(error => {
            console.error("Sharing failed:", error);
          });
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Sharing failed:", error);
        }
      }
    } else {
      const result: boolean = await this.windowRefService.copyToClipboard(this.link);

      if (!result) {
        this.popNotificationsService.error(this.translateService.instant("Failed to copy link to clipboard."));
        return;
      }

      this.popNotificationsService.info(this.translateService.instant("Link copied to clipboard."));
    }
  }

  onReportClicked(event: Event): void {
    event.preventDefault();

    if (!this.comment || this.comment.deleted) {
      return;
    }

    if (this.reportAbuseModal) {
      this.reportAbuseModal.open({
        contentType: "comment",
        contentId: this.comment.id,
        authorName: this.comment.authorDisplayName || this.comment.authorUsername,
        onSuccess: () => {
          // Mark comment as reported (not deleted) - show under review message
          // Note: Using the generic API means the comment isn't auto-deleted
          // We'll show it as reported but keep the content visible until moderation
          this.comment.pendingModeration = true;
          this.html = this.domSanitizer.sanitize(
            1,
            "<p class='text-muted font-italic'>This comment is currently under review for reported content.</p>"
          );
          this.changeDetectorRef.markForCheck();
        }
      });
    } else {
      // Fallback if modal isn't available
      this.popNotificationsService.warning(
        this.translateService.instant("Report feature is not available at this moment.")
      );
    }
  }

  onTranslateClicked(event: Event) {
    event.preventDefault();

    this.translating = true;

    // Check if we have a cached translation
    const isTranslated = this.contentTranslateService.hasTranslation("comment", this.comment.id.toString());
    if (isTranslated) {
      this.translated = true;
      this._loadTranslatedContent();
      return;
    }

    // Otherwise perform new translation
    this.contentTranslateService
      .translate({
        text: this.comment.text,
        sourceLanguage: this.comment.detectedLanguage,
        format: "bbcode",
        itemType: "comment",
        itemId: this.comment.id.toString()
      })
      .subscribe(
        translatedHtml => {
          this.html = translatedHtml;
          this.translating = false;
          this.translated = true;
          this.changeDetectorRef.markForCheck();
        },
        error => {
          this.translating = false;
          this.changeDetectorRef.markForCheck();
        }
      );
  }

  onSeeOriginalClicked(event: Event) {
    event.preventDefault();

    // Clear translation preference in localStorage
    this.contentTranslateService.clearTranslation("comment", this.comment.id.toString());

    this._updateHtml();
    this.translated = false;
  }

  private _loadTranslatedContent(): void {
    this.contentTranslateService
      .translate({
        text: this.comment.text,
        sourceLanguage: this.comment.detectedLanguage,
        format: "bbcode",
        itemType: "comment",
        itemId: this.comment.id.toString()
      })
      .subscribe(
        translatedHtml => {
          this.html = translatedHtml;
          this.translating = false;
          this.changeDetectorRef.markForCheck();

          // Check for code blocks in translated content and apply highlighting if needed
          if (this._isBrowser && this.highlightService.needsHighlighting(translatedHtml as string)) {
            // Use utilsService.delay to wait for the DOM to update with the translated HTML
            this.utilsService.delay(0).subscribe(() => this._initHighlightJs());
          }
        },
        error => {
          this._updateHtml();
          this.translating = false;
          this.translated = false;
          this.changeDetectorRef.markForCheck();
        }
      );
  }

  private _initAvatarUrl(): void {
    this.avatarUrl = UtilsService.convertDefaultAvatar(this.comment.authorAvatar);
  }

  private _initHighlightJs() {
    if (!this._isBrowser) {
      return;
    }

    // Check if the comment content has code blocks that need highlighting
    const commentHtml = this.comment.html || "";

    if (!this.highlightService.needsHighlighting(commentHtml)) {
      // No code blocks found, skip loading highlight.js
      return;
    }

    // Load highlight.js dynamically
    this.highlightService
      .loadHighlightJs()
      .then(() => {
        // After successful loading, apply highlighting to code blocks
        const element = this.elementRef.nativeElement;
        this.highlightService.highlightCodeBlocks(element);
        this.changeDetectorRef.markForCheck();
      })
      .catch(error => {
        console.warn("Failed to load highlight.js:", error);
      });
  }

  private _listenToLikes() {
    this.actions$
      .pipe(
        ofType(AppActionTypes.CREATE_TOGGLE_PROPERTY_SUCCESS),
        map((action: CreateTogglePropertySuccess) => action.payload.toggleProperty),
        filter(
          (toggleProperty: TogglePropertyInterface) =>
            toggleProperty.contentType === this.commentContentType.id && toggleProperty.objectId === this.comment.id
        ),
        takeUntil(this.destroyed$)
      )
      .subscribe(toggleProperty => {
        this.comment.likes = [...this.comment.likes, toggleProperty.user];
        this.changeDetectorRef.markForCheck();
      });

    this.actions$
      .pipe(
        ofType(AppActionTypes.DELETE_TOGGLE_PROPERTY_SUCCESS),
        map((action: DeleteTogglePropertySuccess) => action.payload.toggleProperty),
        filter(
          (toggleProperty: TogglePropertyInterface) =>
            toggleProperty.contentType === this.commentContentType.id && toggleProperty.objectId === this.comment.id
        ),
        takeUntil(this.destroyed$)
      )
      .subscribe(toggleProperty => {
        this.comment.likes = this.comment.likes.filter(user => user !== toggleProperty.user);
        this.changeDetectorRef.markForCheck();
      });
  }

  private _initEditFields() {
    // Determine which editor to use based on comment content
    const isNewStyleComment = !!this.comment?.textProsemirror;

    if (isNewStyleComment) {
      // Use TipTap for new-style comments
      this.editFields = [
        {
          key: "comment",
          type: "tiptap",
          wrappers: ["default-wrapper"],
          id: `edit-comment-field-${this.comment.id}`,
          props: {
            required: true,
            autofocus: "end",
            height: "200px",
            placeholder: this.translateService.instant("Edit your comment..."),
            onContentChange: ({ json, html, text }) => {
              // Store all formats for backend submission
              this.editModel.commentHtml = html;
              this.editModel.commentText = text;
            }
          }
        }
      ];
    } else {
      // Use CKEditor for legacy BBCode comments
      this.editFields = [
        {
          key: "comment",
          type: "ckeditor",
          wrappers: ["default-wrapper"],
          id: `edit-comment-field-${this.comment.id}`,
          props: {
            required: true
          }
        }
      ];
    }
  }

  private _initReplyFields() {
    // Always use TipTap for new replies
    this.replyFields = [
      {
        key: "commentReply",
        type: "tiptap",
        wrappers: ["default-wrapper"],
        id: `reply-comment-field-${this.comment.id}`,
        props: {
          required: true,
          autofocus: true,
          height: "200px",
          placeholder: this.translateService.instant("Write your reply..."),
          onContentChange: ({ json, html, text }) => {
            // Store all formats for backend submission
            this.replyModel.commentReplyHtml = html;
            this.replyModel.commentReplyText = text;
          }
        }
      }
    ];
  }

  private _initReplyOffcanvasFields() {
    this.replyOffcanvasFields = [
      {
        key: "commentReply",
        type: "tiptap",
        wrappers: ["default-wrapper"],
        props: {
          required: true,
          placeholder: this.translateService.instant("Write your reply..."),
          onContentChange: ({ json, html, text }) => {
            // Store JSON as string for the form field (required for validation)
            this.replyModel.commentReply = JSON.stringify(json);
            // Store additional formats for backend submission
            this.replyModel.commentReplyHtml = html;
            this.replyModel.commentReplyText = text;
          }
        }
      }
    ];
  }

  private _initHighlighted() {
    const hash = this.windowRefService.getCurrentUrl().hash || "";

    // Support both #c123 and #imageId-c123 fragment formats
    if (hash === `#c${this.comment.id}` || (hash && hash.endsWith(`-c${this.comment.id}`))) {
      this.highlighted = true;
    }

    if (this.highlighted) {
      this.windowRefService.scrollToElement(`#c${this.comment.id}`, {
        behavior: "smooth",
        block: "center",
        inline: "nearest"
      });
    }
  }

  private _initMarginLeft(): string {
    if (!this._elementWidth) {
      this.margin = `0px`;
      return;
    }

    const minContentWidth = 300;
    const maxMargin = this._elementWidth - minContentWidth;
    const margin = Math.min(maxMargin, (this.comment.depth - 1) * 16);
    this.margin = `${margin}px`;
  }

  private _initLink(): void {
    const url = this.windowRefService.getCurrentUrl();

    // If the existing hash is in the format #imageId-cCommentId,
    // maintain the imageId part when generating this comment's link
    const currentHash = url.hash || "";
    const imageIdMatch = currentHash.match(/^#(\d+)-c\d+$/);

    if (imageIdMatch) {
      const imageId = imageIdMatch[1];
      url.hash = `${imageId}-c${this.comment.id}`;
    } else {
      // Default to the standard c{id} format
      url.hash = `c${this.comment.id}`;
    }

    this.link = url.href;
  }

  private _initUserGalleryUrl(): void {
    this.currentUserWrapper$.pipe(take(1)).subscribe(currentUserWrapper => {
      this.userGalleryUrl = this.userService.getGalleryUrl(
        this.comment.authorUsername,
        !currentUserWrapper.userProfile || currentUserWrapper.userProfile.enableNewGalleryExperience
      );
    });
  }

  private _updateHtml(): void {
    // If the content is already translated in localStorage, load the translation
    if (this.contentTranslateService.hasTranslation("comment", this.comment.id.toString())) {
      this.translated = true;
      this._loadTranslatedContent();
    } else {
      // Otherwise use the original HTML
      this.html = this.contentTranslateService.sanitizeContent(this.comment.html, "html");
    }

    // If we're in the browser and the component has been rendered, check for code blocks
    if (this._isBrowser && this.elementRef?.nativeElement) {
      // Use utilsService.delay to wait for the DOM to update with the new HTML
      this.utilsService.delay(0).subscribe(() => this._initHighlightJs());
    }
  }

  private _setupFormListeners(): void {
    // Monitor reply form
    this.replyForm.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.formDirtyChange.emit(this.isDirty());
    });

    // Monitor edit form
    this.editForm.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.formDirtyChange.emit(this.isDirty());
    });
  }
}
